import React, { useState, useEffect } from 'react';
import { novedadService } from '../services/novedadService';
import { RegistroNovedad } from '../types/novedad';

interface NovedadesHistoryProps {
  empleadoId: string;
}

export const NovedadesHistory: React.FC<NovedadesHistoryProps> = ({ empleadoId }) => {
  const [novedades, setNovedades] = useState<RegistroNovedad[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );

  useEffect(() => {
    loadNovedades();
  }, [selectedDate]);

  const loadNovedades = async () => {
    try {
      setLoading(true);
      const registros = await novedadService.getNovedadesEmpleado(empleadoId);
      
      // Filtrar por fecha seleccionada
      const filteredNovedades = registros.filter(novedad => {
        const novedadDate = new Date(novedad.fecha_hora_inicio).toISOString().split('T')[0];
        return novedadDate === selectedDate;
      });
      
      setNovedades(filteredNovedades);
    } catch (error) {
      console.error('Error al cargar novedades:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date: string) => {
    return new Date(date).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDuration = (inicio: string, fin?: string) => {
    if (!fin) return 'En curso';
    
    const start = new Date(inicio);
    const end = new Date(fin);
    const diffInMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutos`;
    }
    
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Historial de Novedades
        </h2>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        </div>
      ) : novedades.length > 0 ? (
        <div className="space-y-4">
          {novedades.map((novedad) => (
            <div
              key={novedad.id}
              className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium text-gray-900">
                    {novedad.tipo_novedad.nombre}
                  </h3>
                  {novedad.observacion && (
                    <p className="text-sm text-gray-500 mt-1">
                      {novedad.observacion}
                    </p>
                  )}
                </div>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    novedad.estado === 'En curso'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {novedad.estado}
                </span>
              </div>
              <div className="mt-2 text-sm text-gray-500">
                <p>
                  Inicio: {formatTime(novedad.fecha_hora_inicio)}
                </p>
                {novedad.fecha_hora_fin && (
                  <p>
                    Fin: {formatTime(novedad.fecha_hora_fin)}
                  </p>
                )}
                <p className="mt-1 font-medium">
                  Duración: {formatDuration(novedad.fecha_hora_inicio, novedad.fecha_hora_fin)}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500">
          No hay novedades registradas para esta fecha
        </div>
      )}
    </div>
  );
};
